import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  Alert,
  LinearProgress,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'

interface BulkOperationDialogProps {
  open: boolean
  onClose: () => void
  operation: 'approve' | 'reject' | 'update-status' | 'assign' | null
  selectedCount: number
  onConfirm: (data?: any) => Promise<void>
}

const BulkOperationDialog = ({
  open,
  onClose,
  operation,
  selectedCount,
  onConfirm,
}: BulkOperationDialogProps) => {
  const [reason, setReason] = useState('')
  const [assignTo, setAssignTo] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleConfirm = async () => {
    if (operation === 'reject' && !reason.trim()) {
      setError('Rejection reason is required')
      return
    }
    if (operation === 'assign' && !assignTo.trim()) {
      setError('Assignee is required')
      return
    }
    if (operation === 'update-status' && !status.trim()) {
      setError('Status is required')
      return
    }

    setLoading(true)
    setError('')
    try {
      const data: any = {}
      if (operation === 'reject') data.reason = reason
      if (operation === 'assign') data.assignTo = assignTo
      if (operation === 'update-status') data.status = status

      await onConfirm(data)
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Operation failed')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setReason('')
    setAssignTo('')
    setStatus('')
    setError('')
    onClose()
  }

  const getTitle = () => {
    switch (operation) {
      case 'approve':
        return `Approve ${selectedCount} Transaction${selectedCount > 1 ? 's' : ''}?`
      case 'reject':
        return `Reject ${selectedCount} Transaction${selectedCount > 1 ? 's' : ''}?`
      case 'assign':
        return `Assign ${selectedCount} Transaction${selectedCount > 1 ? 's' : ''}`
      case 'update-status':
        return `Update Status for ${selectedCount} Transaction${selectedCount > 1 ? 's' : ''}`
      default:
        return 'Bulk Operation'
    }
  }

  const getIcon = () => {
    switch (operation) {
      case 'approve':
        return <CheckCircleIcon color="success" />
      case 'reject':
        return <CancelIcon color="error" />
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          {getIcon()}
          <Typography variant="h6">{getTitle()}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {operation === 'reject' && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Rejection Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              required
            />
          )}

          {operation === 'assign' && (
            <TextField
              fullWidth
              label="Assign To"
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              placeholder="Enter operator name or email"
              required
            />
          )}

          {operation === 'update-status' && (
            <TextField
              fullWidth
              select
              label="New Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              SelectProps={{
                native: true,
              }}
              required
            >
              <option value="">Select status</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
            </TextField>
          )}

          {operation === 'approve' && (
            <Alert severity="info">
              This will approve {selectedCount} transaction{selectedCount > 1 ? 's' : ''}. This action cannot be undone.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={operation === 'reject' ? 'error' : operation === 'approve' ? 'success' : 'primary'}
          disabled={loading}
          startIcon={getIcon()}
        >
          {operation === 'approve' ? 'Approve' : operation === 'reject' ? 'Reject' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BulkOperationDialog


